import { useQuery } from '@tanstack/react-query';
import { FaUserSecret, FaEye, FaLock, FaShieldAlt } from 'react-icons/fa';
import { MdAccessTime } from 'react-icons/md';
import api from '../../services/api';

const MODE_STYLE = {
  readonly:    { label: 'Read-Only',   bg: '#374151',  color: '#9ca3af', Icon: FaEye  },
  full_access: { label: 'Full Access', bg: '#7f1d1d',  color: '#fca5a5', Icon: FaLock },
};

const STATUS_STYLE = {
  active:  { bg: '#14532d', color: '#4ade80' },
  ended:   { bg: '#374151', color: '#9ca3af' },
  // Expired reads as a calm, closed state (muted slate ≈ bg-slate-500/15), like Ended.
  expired: { bg: 'rgba(100,116,139,0.15)', color: '#94a3b8' },
};

const formatDuration = (startedAt, endedAt) => {
  if (!endedAt) return '—';
  const mins = Math.round((new Date(endedAt) - new Date(startedAt)) / 60000);
  if (mins < 1)  return '<1m';
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

const fmt = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export default function SupportAccessLog() {
  const { data, isPending } = useQuery({
    queryKey: ['support-access-log'],
    queryFn:  () => api.get('/api/admin/support-access-log').then(r => r.data),
  });

  const sessions = data?.sessions || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-secondary flex items-center gap-2">
          <FaShieldAlt className="text-primary" /> Support Access Log
        </h1>
        <p className="text-sm mt-1 text-muted">
          Every time a TrainAxis support agent accessed your club account. All sessions are read-only by default and fully logged.
        </p>
      </div>

      {/* Transparency notice */}
      <div className="rounded-xl border p-4 flex gap-3" style={{ borderColor: 'var(--color-divider)', background: 'var(--color-card)' }}>
        <FaShieldAlt className="text-primary flex-shrink-0 mt-0.5 text-lg" />
        <div className="text-sm text-muted space-y-1">
          <p className="font-semibold text-secondary">Why can you see this?</p>
          <p>TrainAxis platform support may access your admin portal to diagnose issues or assist you. We make every access fully transparent. Read-only sessions cannot make changes to your data. Full-access sessions will always have a documented reason.</p>
        </div>
      </div>

      {isPending ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-7 h-7 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : sessions.length === 0 ? (
        <div className="rounded-xl border p-12 text-center" style={{ borderColor: 'var(--color-divider)', background: 'var(--color-card)' }}>
          <FaUserSecret className="mx-auto text-4xl mb-3 text-muted" />
          <p className="font-semibold text-secondary">No support access recorded</p>
          <p className="text-sm text-muted mt-1">Your club has not been accessed by support staff.</p>
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--color-divider)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr style={{ background: 'var(--color-card)', borderBottom: '1px solid var(--color-divider)' }}>
                  {['Support Agent', 'Mode', 'Reason', 'Started', 'Duration', 'Status'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sessions.map(s => {
                  const m  = MODE_STYLE[s.mode]   || MODE_STYLE.readonly;
                  // Safety net: a stale 'active' session past its TTL shows as Expired,
                  // even if the backend sweep hasn't run yet.
                  const effStatus = (s.status === 'active' && s.expiresAt && new Date(s.expiresAt).getTime() < Date.now())
                    ? 'expired'
                    : s.status;
                  const st = STATUS_STYLE[effStatus] || STATUS_STYLE.ended;
                  const Icon = m.Icon;
                  return (
                    <tr key={s._id} className="border-b border-divider hover:bg-nav-hover transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-secondary">{s.superAdminName}</div>
                        <div className="text-xs text-muted">TrainAxis Support</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full"
                          style={{ background: m.bg, color: m.color }}>
                          <Icon className="text-xs" />
                          {m.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 max-w-[220px]">
                        <p className="text-sm text-muted leading-relaxed">{s.reason || '—'}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted whitespace-nowrap">
                        <div>{fmt(s.startedAt)}</div>
                        {s.endedAt && <div className="mt-0.5 text-subtle">Ended {fmt(s.endedAt)}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 text-muted text-xs">
                          <MdAccessTime />
                          {formatDuration(s.startedAt, s.endedAt)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full capitalize"
                          style={{ background: st.bg, color: st.color }}>
                          {effStatus}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs text-muted text-right">
        {sessions.length} session{sessions.length !== 1 ? 's' : ''} total
      </p>
    </div>
  );
}
